/* WhatsApp share & invite links.
   wa.me with the message prefilled. Give a phone and it opens that contact's
   chat; omit it and WhatsApp asks the sender to pick — we never store a number
   just to be able to send. */

/** Digits only — wa.me rejects spaces, +, dashes and brackets. */
export function waPhone(phone?: string): string {
  return (phone || "").replace(/\D/g, "");
}

/** wa.me link with `text` prefilled, aimed at `phone` when we have one. */
export function waUrl(text: string, phone?: string): string {
  return `https://wa.me/${waPhone(phone)}?text=${encodeURIComponent(text)}`;
}

/** Share with whoever the sender picks. */
export function waShareUrl(text: string): string {
  return waUrl(text);
}

export function openWhatsApp(text: string, phone?: string) {
  window.open(waUrl(text, phone), "_blank", "noopener");
}

/* ---------------------------------------------------------------- invites */

export interface InviteParts {
  name: string;      // who we're inviting
  studio: string;    // the studio's name
  project: string;
  url: string;       // portal link
  code: string;      // their personal access code
  can?: string;      // a natural sentence of the features they can access
}

/** The client's portal invitation — a brief intro, the link + code, and only the
    features they actually have access to. */
export function clientInviteText(o: InviteParts): string {
  const can = o.can || "follow your project in one place";
  return (
    `Hi ${o.name.split(" ")[0]} — ${o.studio} has set up your private online portal for ${o.project}.\n\n` +
    `It's your one place to ${can}.\n\n` +
    `Open it: ${o.url}\n` +
    `Your access code: ${o.code}\n\n` +
    `The code is personal to you — please don't pass it on.\n\n— ${o.studio}`
  );
}

/** The same invitation as email copy (subject/heading come from the caller). */
export function clientInviteEmail(o: InviteParts): string {
  const can = o.can || "follow your project in one place";
  return (
    `${o.studio} has set up your private online portal for ${o.project} — your one place to ${can}. ` +
    `Sign in with the personal access code ${o.code}. ` +
    `Please keep the code to yourself, as it's unique to you.`
  );
}

export function waClientInviteUrl(o: InviteParts, phone?: string): string {
  return waUrl(clientInviteText(o), phone);
}

/** A team member's invitation to register — they set their own password. */
export function teamInviteText(o: { name: string; studio: string; role: string; url: string }): string {
  return (
    `Hi ${o.name} — you're invited to join ${o.studio} on the studio cockpit as ${o.role}.\n\n` +
    `Set up your account here: ${o.url}\n\n` +
    `Use your own email and pick your own password. The link works once and is just for you.`
  );
}
